import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Star, Minus, Plus, Truck, ShieldCheck, RotateCcw, ChevronRight, Check, ShoppingCart,
    Droplets, Sparkles, Wind, Leaf, FlaskConical, HeartHandshake,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import api from '@/lib/api';
import ProductCard from '@/components/ProductCard';
import { Reveal } from '@/components/motion/Primitives';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/context/CartContext';
import { formatINR, resolveImage } from '@/lib/format';
import { toast } from 'sonner';

/* ------------------- category-based ingredient & how-it-works storytelling */
const STORY_BY_CATEGORY = {
    'Laundry Care': {
        steps: [
            { icon: Droplets, t: 'Dose it right', d: 'One capful delivers concentrated, low-suds cleaning power.' },
            { icon: Sparkles, t: 'Lift the stains', d: 'Active enzymes dissolve sweat, oil and food deep in the fibres.' },
            { icon: Wind, t: 'Fresh finish', d: 'Rinses clean, leaving clothes bright, soft and fragrant for days.' },
        ],
        ingredients: [
            { icon: FlaskConical, name: 'Enzyme stain-lifters', benefit: 'Break down stubborn stains' },
            { icon: Leaf, name: 'Plant-based surfactants', benefit: 'Effective yet gentle clean' },
            { icon: HeartHandshake, name: 'Fabric conditioners', benefit: 'Softness that lasts' },
        ],
    },
    'Dishwashing': {
        steps: [
            { icon: Droplets, t: 'A little goes far', d: 'A single drop whips up a rich, long-lasting lather.' },
            { icon: Sparkles, t: 'Cut the grease', d: 'Powerful actives melt through ghee, oil and masala instantly.' },
            { icon: Wind, t: 'Rinse sparkling', d: 'Washes off fast — no film, no smell, just shine.' },
        ],
        ingredients: [
            { icon: FlaskConical, name: 'Grease-cutting actives', benefit: 'Dissolve oil instantly' },
            { icon: Leaf, name: 'Coconut-derived cleansers', benefit: 'Kind to your skin' },
            { icon: HeartHandshake, name: 'Glycerin moisturisers', benefit: 'Protects your hands' },
        ],
    },
    'Surface & Glass': {
        steps: [
            { icon: Droplets, t: 'Spray evenly', d: 'A fine mist covers the surface without drips or waste.' },
            { icon: Sparkles, t: 'Wipe away grime', d: 'Lifts dust, fingerprints and smudges in a single pass.' },
            { icon: Wind, t: 'Streak-free shine', d: 'Dries fast to a crystal-clear, spotless finish.' },
        ],
        ingredients: [
            { icon: FlaskConical, name: 'Fast-evaporating actives', benefit: 'No streaks or residue' },
            { icon: Sparkles, name: 'Anti-smudge agents', benefit: 'Glass stays clearer longer' },
            { icon: Leaf, name: 'Fresh fragrance', benefit: 'Rooms smell clean' },
        ],
    },
    'Bathroom & Utility': {
        steps: [
            { icon: Droplets, t: 'Apply the gel', d: 'Thick formula clings to surfaces and reaches under the rim.' },
            { icon: ShieldCheck, t: 'Let it work', d: 'Actives dissolve limescale and kill 99.9% of germs.' },
            { icon: Wind, t: 'Rinse away', d: 'Flushes off grime, stains and odour for a sparkling finish.' },
        ],
        ingredients: [
            { icon: FlaskConical, name: 'Active descalers', benefit: 'Dissolve tough limescale' },
            { icon: ShieldCheck, name: 'Germ-kill actives', benefit: 'Kills 99.9% of germs' },
            { icon: Leaf, name: 'Thick-cling gel', benefit: 'Clings for a deeper clean' },
        ],
    },
    'Hand Wash': {
        steps: [
            { icon: Droplets, t: 'Lather up', d: 'A single pump creates a rich, creamy foam.' },
            { icon: ShieldCheck, t: 'Wash 20 seconds', d: 'Germ-shield actives wash away 99.9% of germs.' },
            { icon: HeartHandshake, t: 'Soft, clean hands', d: 'Rinses clean, leaving hands soft and moisturised.' },
        ],
        ingredients: [
            { icon: ShieldCheck, name: 'Germ-shield actives', benefit: 'Kills 99.9% of germs' },
            { icon: Leaf, name: 'Gentle cleansers', benefit: 'Non-drying formula' },
            { icon: HeartHandshake, name: 'Glycerin & moisturisers', benefit: 'Softness every wash' },
        ],
    },
};

const STORY_FALLBACK = STORY_BY_CATEGORY['Dishwashing'];
const PROMISES = ['Skin-friendly', 'No harsh residue', 'Long-lasting freshness', 'Made in India · Since 1987'];

const ProductStory = ({ category }) => {
    const story = STORY_BY_CATEGORY[category] || STORY_FALLBACK;
    return (
        <section data-testid="pdp-story" className="mt-16 lg:mt-24">
            {/* How it works */}
            <Reveal>
                <div className="text-center max-w-2xl mx-auto">
                    <p className="kicker text-[var(--xtra-blue)]">How it works</p>
                    <h2 className="mt-3 font-display font-extrabold tracking-tight leading-[1.05] text-2xl sm:text-3xl lg:text-4xl">
                        The science of a proper clean
                    </h2>
                    <p className="mt-3 text-[var(--text-muted)]">Three simple steps to a spotless, fresh result — every single time.</p>
                </div>
            </Reveal>
            <div className="mt-9 grid sm:grid-cols-3 gap-4 sm:gap-6">
                {story.steps.map((s, i) => (
                    <Reveal key={s.t} delay={i * 0.08}>
                        <div className="relative h-full rounded-[var(--radius-lg)] border border-[var(--border)] bg-white p-6 shadow-[var(--shadow-tight)]">
                            <span className="absolute right-5 top-4 font-display font-extrabold text-4xl text-[var(--bg-soft)] leading-none select-none">{i + 1}</span>
                            <span className="h-12 w-12 rounded-2xl bg-[var(--xtra-blue)] text-white grid place-items-center">
                                <s.icon className="h-6 w-6" />
                            </span>
                            <h3 className="mt-4 font-display font-bold text-lg">{s.t}</h3>
                            <p className="mt-1.5 text-sm text-[var(--text-muted)] leading-relaxed">{s.d}</p>
                        </div>
                    </Reveal>
                ))}
            </div>

            {/* What's inside */}
            <div className="mt-12 lg:mt-16 rounded-[var(--radius-lg)] royal-panel relative overflow-hidden p-6 sm:p-10">
                <div className="noise-overlay" />
                <div className="relative grid lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-12 items-center">
                    <Reveal>
                        <div>
                            <p className="kicker text-[var(--xtra-yellow)]">What's inside</p>
                            <h2 className="mt-3 font-display font-extrabold tracking-tight leading-[1.02] text-2xl sm:text-3xl lg:text-4xl text-white">
                                Powerful actives,<br />thoughtfully balanced.
                            </h2>
                            <p className="mt-4 text-white/70 leading-relaxed max-w-md">
                                Every Xtra formula pairs serious cleaning performance with care for your skin, your things and your home.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-2">
                                {PROMISES.map((p) => (
                                    <span key={p} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs font-semibold text-white">
                                        <Check className="h-3.5 w-3.5 text-[var(--xtra-yellow)]" /> {p}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                    <div className="grid gap-3">
                        {story.ingredients.map((ing, i) => (
                            <Reveal key={ing.name} delay={i * 0.08}>
                                <div className="flex items-center gap-4 rounded-2xl bg-white/95 backdrop-blur p-4">
                                    <span className="h-11 w-11 rounded-xl bg-[var(--bg-soft)] text-[var(--xtra-blue)] grid place-items-center shrink-0">
                                        <ing.icon className="h-5 w-5" />
                                    </span>
                                    <div className="min-w-0">
                                        <p className="font-display font-bold text-[15px] text-black leading-tight">{ing.name}</p>
                                        <p className="text-sm text-[var(--text-muted)]">{ing.benefit}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default function ProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const reduce = useReducedMotion();
    const { addItem, setDrawerOpen } = useCart();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [qty, setQty] = useState(1);

    useEffect(() => {
        setLoading(true);
        setQty(1);
        api.get(`/products/${slug}`)
            .then(({ data }) => setData(data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) {
        return (
            <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-12 grid lg:grid-cols-2 gap-10">
                <Skeleton className="aspect-square rounded-[var(--radius-lg)]" />
                <div className="space-y-4"><Skeleton className="h-10 w-3/4" /><Skeleton className="h-6 w-1/2" /><Skeleton className="h-24 w-full" /><Skeleton className="h-12 w-full" /></div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-24 text-center">
                <h1 className="font-display text-2xl font-bold">Product not found</h1>
                <Link to="/shop" className="btn-primary mt-6 inline-flex h-12 px-6">Back to shop</Link>
            </div>
        );
    }

    const p = data.product;
    const discount = p.compare_at_price && p.compare_at_price > p.price
        ? Math.round(((p.compare_at_price - p.price) / p.compare_at_price) * 100) : 0;

    const add = () => { addItem(p, qty); toast.success(`${p.name} added to cart`); };
    const buyNow = () => { addItem(p, qty); setDrawerOpen(false); navigate('/checkout'); };

    return (
        <div className="relative overflow-x-clip pb-24 lg:pb-14">
            <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
                {/* Breadcrumb */}
                <nav className="flex items-center gap-1.5 text-sm text-[var(--text-muted)]" aria-label="Breadcrumb">
                    <Link to="/" className="hover:text-black transition-colors">Home</Link><ChevronRight className="h-4 w-4" />
                    <Link to="/shop" className="hover:text-black transition-colors">Shop</Link><ChevronRight className="h-4 w-4" />
                    <span className="text-black font-medium truncate">{p.name}</span>
                </nav>

                <div className="mt-6 grid lg:grid-cols-2 gap-8 lg:gap-16">
                    {/* Gallery */}
                    <div className="lg:sticky lg:top-28 self-start">
                        <div data-testid="pdp-image-gallery" className="relative rounded-[var(--radius-lg)] product-stage border border-[var(--border)] overflow-hidden aspect-square grid place-items-center p-8">
                            {p.badge && <Badge className="absolute z-10 left-4 top-4 bg-[var(--xtra-yellow)] text-black hover:bg-[var(--xtra-yellow)] border-0 font-bold">{p.badge}</Badge>}
                            {discount > 0 && <Badge className="absolute z-10 right-4 top-4 bg-[var(--xtra-blue)] text-white hover:bg-[var(--xtra-blue)] border-0 font-bold">-{discount}%</Badge>}
                            <motion.img
                                key={p.id}
                                src={resolveImage(p.image)}
                                alt={p.name}
                                fetchpriority="high"
                                decoding="async"
                                initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: 16 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                className="relative z-0 h-full w-full object-contain drop-shadow-[0_30px_50px_rgba(13,2,120,0.20)]"
                            />
                        </div>
                        {/* trust row */}
                        <div className="mt-4 grid grid-cols-3 gap-3">
                            {[{ icon: Truck, t: 'Free shipping', s: 'over ₹499' }, { icon: ShieldCheck, t: 'Safe & tested', s: 'quality assured' }, { icon: RotateCcw, t: 'Easy returns', s: '7-day policy' }].map((f, i) => (
                                <div key={i} className="rounded-2xl border border-[var(--border)] bg-white p-3 text-center"><f.icon className="h-5 w-5 mx-auto text-[var(--xtra-blue)]" /><p className="text-xs font-semibold mt-1.5">{f.t}</p><p className="text-[11px] text-[var(--text-muted)]">{f.s}</p></div>
                            ))}
                        </div>
                    </div>

                    {/* Buy box */}
                    <div>
                        <p className="kicker text-[var(--xtra-blue)]">{p.category}</p>
                        <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-3xl lg:text-4xl mt-2">{p.name}</h1>
                        <p className="text-lg text-[var(--text-muted)] mt-2">{p.tagline}</p>

                        <div className="mt-3 flex items-center gap-2 text-sm">
                            <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-4 w-4 ${i < Math.round(p.rating) ? 'fill-[var(--xtra-yellow)] text-[var(--xtra-yellow)]' : 'text-gray-300'}`} />)}</div>
                            <span className="font-semibold tabular-nums">{p.rating}</span>
                            <span className="text-[var(--text-muted)]">· {p.reviews_count} reviews</span>
                        </div>

                        <div className="mt-5 flex items-baseline gap-3">
                            <span data-testid="pdp-price" className="font-display text-3xl font-extrabold tabular-nums">{formatINR(p.price)}</span>
                            {discount > 0 && (<><span className="text-lg text-[var(--text-muted)] line-through tabular-nums">{formatINR(p.compare_at_price)}</span><Badge className="bg-blue-50 text-[var(--xtra-blue)] hover:bg-blue-50 border-0">Save {discount}%</Badge></>)}
                            <span className="text-sm text-[var(--text-muted)] ml-1">/ {p.size}</span>
                        </div>
                        <p className="mt-1 text-sm text-[var(--text-muted)]">Inclusive of all taxes</p>

                        <p className="mt-6 text-[15px] text-black leading-relaxed">{p.description}</p>

                        {/* Qty + actions */}
                        <div className="mt-7 flex items-center gap-3">
                            <div className="inline-flex items-center rounded-full border border-[var(--border)]">
                                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-12 w-12 grid place-items-center hover:bg-[var(--bg-soft)] rounded-l-full transition-colors" aria-label="Decrease quantity"><Minus className="h-4 w-4" /></button>
                                <span data-testid="pdp-quantity-input" className="w-10 text-center font-bold tabular-nums">{qty}</span>
                                <button onClick={() => setQty((q) => q + 1)} className="h-12 w-12 grid place-items-center hover:bg-[var(--bg-soft)] rounded-r-full transition-colors" aria-label="Increase quantity"><Plus className="h-4 w-4" /></button>
                            </div>
                            <button data-testid="pdp-add-to-cart-button" onClick={add} className="btn-primary flex-1 h-12">Add to cart</button>
                        </div>
                        <button data-testid="pdp-buy-now-button" onClick={buyNow} className="btn-accent w-full h-12 mt-3">Buy it now</button>

                        {/* Benefits */}
                        {p.benefits?.length > 0 && (
                            <div className="mt-8">
                                <h3 className="font-display font-bold text-lg">Key benefits</h3>
                                <ul className="mt-3 grid sm:grid-cols-2 gap-2.5">
                                    {p.benefits.map((b, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm"><span className="h-5 w-5 rounded-full bg-[var(--xtra-yellow)] grid place-items-center shrink-0 mt-0.5"><Check className="h-3.5 w-3.5 text-black" /></span>{b}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Details accordion */}
                        <Accordion type="single" collapsible className="mt-8" defaultValue="how">
                            {p.how_to_use && (<AccordionItem value="how"><AccordionTrigger className="font-display font-bold">How to use</AccordionTrigger><AccordionContent className="text-[var(--text-muted)]">{p.how_to_use}</AccordionContent></AccordionItem>)}
                            {p.ingredients && (<AccordionItem value="ing"><AccordionTrigger className="font-display font-bold">Ingredients</AccordionTrigger><AccordionContent className="text-[var(--text-muted)]">{p.ingredients}</AccordionContent></AccordionItem>)}
                            <AccordionItem value="ship"><AccordionTrigger className="font-display font-bold">Shipping & returns</AccordionTrigger><AccordionContent className="text-[var(--text-muted)]">Free shipping on orders over ₹499. Dispatched within 24 hours. Easy 7-day returns on unopened products.</AccordionContent></AccordionItem>
                        </Accordion>
                    </div>
                </div>

                {/* Storytelling: how it works + what's inside */}
                <ProductStory category={p.category} />

                {/* Related */}
                {data.related?.length > 0 && (
                    <div className="mt-16 lg:mt-24">
                        <Reveal>
                            <h2 className="font-display font-extrabold tracking-tight text-2xl lg:text-3xl">You may also like</h2>
                        </Reveal>
                        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                            {data.related.map((r, i) => (
                                <Reveal key={r.id} delay={(i % 4) * 0.05}><ProductCard product={r} index={i} /></Reveal>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile sticky add-to-cart bar */}
            <div className="sticky-bar lg:hidden">
                <div className="px-4 py-3 flex items-center gap-3">
                    <div className="min-w-0">
                        <p className="text-xs text-[var(--text-muted)] leading-none">{p.size}</p>
                        <p className="font-display font-extrabold text-lg tabular-nums leading-tight">{formatINR(p.price)}</p>
                    </div>
                    <button data-testid="pdp-add-to-cart-sticky" onClick={add} className="btn-primary flex-1 h-12"><ShoppingCart className="h-4 w-4" /> Add to cart</button>
                </div>
            </div>
        </div>
    );
}
